import { useEffect, useState } from "react";
import { Wallet, Landmark, Users, Trophy, Briefcase } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US")}`;

export default function EconomyStats() {
  const [eco, setEco] = useState(null);

  useEffect(() => {
    const load = () => api.get("/economy").then((res) => setEco(res.data)).catch(() => {});
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!eco) return null;

  if (!eco.available) {
    return (
      <div className="rounded-2xl border border-dashed border-sunset-amber/30 bg-plum-700/40 p-8 text-center" data-testid="economy-unavailable">
        <p className="text-muted-foreground text-sm">
          City economy sync pending — live player & economy data appears here once the server database link is active.
        </p>
      </div>
    );
  }

  const cards = [
    { testid: "economy-total-cash", icon: Wallet, label: "Cash on the Streets", value: fmt(eco.total_cash) },
    { testid: "economy-total-bank", icon: Landmark, label: "Held in Banks", value: fmt(eco.total_bank) },
    { testid: "economy-citizens", icon: Users, label: "Registered Citizens", value: eco.citizens.toLocaleString() },
  ];

  return (
    <div data-testid="economy-panel">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        {cards.map((c, i) => (
          <div
            key={c.testid}
            data-testid={c.testid}
            className="glow-card rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-5 sm:p-6 animate-fade-up"
            style={{ animationDelay: `${i * 90}ms` }}
          >
            <div className="flex items-center justify-between mb-3">
              <c.icon className="h-5 w-5 text-sunset-amber" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{c.label}</span>
            </div>
            <p className="font-mono text-2xl sm:text-3xl font-extrabold text-sunset-gold">{c.value}</p>
          </div>
        ))}
      </div>
      {(eco.richest?.length > 0 || eco.businesses?.length > 0) && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {eco.richest?.length > 0 && (
            <div className="rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-5 sm:p-6" data-testid="economy-richest">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="h-5 w-5 text-sunset-gold" />
                <h3 className="font-display text-lg sm:text-xl font-bold uppercase tracking-wide text-cream">Richest Citizens</h3>
              </div>
              <div className="divide-y divide-sunset-amber/10">
                {eco.richest.map((r, i) => (
                  <div key={i} data-testid={`economy-richest-row-${i}`} className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-3">
                      <span className={`font-mono text-sm font-bold w-7 ${i === 0 ? "text-sunset-gold" : "text-muted-foreground"}`}>#{i + 1}</span>
                      <span className="text-cream font-medium text-sm">{r.name}</span>
                    </div>
                    <span className="font-mono text-sm text-sunset-gold">{fmt(r.total)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {eco.businesses?.length > 0 && (
            <div className="rounded-2xl border border-sunset-amber/20 bg-plum-700/70 p-5 sm:p-6" data-testid="economy-businesses">
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="h-5 w-5 text-sunset-gold" />
                <h3 className="font-display text-lg sm:text-xl font-bold uppercase tracking-wide text-cream">Top Businesses</h3>
              </div>
              <div className="divide-y divide-sunset-amber/10">
                {eco.businesses.map((b, i) => (
                  <div key={i} data-testid={`economy-business-row-${i}`} className="flex items-center justify-between py-2.5">
                    <div className="flex items-center gap-3">
                      <span className={`font-mono text-sm font-bold w-7 ${i === 0 ? "text-sunset-gold" : "text-muted-foreground"}`}>#{i + 1}</span>
                      <div>
                        <span className="text-cream font-medium text-sm capitalize">{b.name}</span>
                        <span className="block text-[10px] text-muted-foreground uppercase tracking-wider">
                          {b.employees} employee{b.employees === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>
                    <span className="font-mono text-sm text-sunset-gold">{fmt(b.balance)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
